import { Component, ElementRef, HostListener } from '@angular/core';
import { Router } from '@angular/router';
import { UserService } from '../../services/user.service';
import { Subscription } from 'rxjs';
import { SharedCaseManagementService } from '../../services/shared-case-management.service';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule } from '@angular/common';
import { NotificationsComponent } from '../../../modules/notifications/notifications.component';
import { FirebaseInitService } from '../../services/firebase-init.service';
import { TTAuthCookieService } from '../../services/ttauth-cookie.service';
import { EgNavBarComponent } from '../eg-nav-bar/eg-nav-bar.component';
import { PortalService } from '../../services/portal.service';
import { TranslationService } from '../../services/translation.service';

@Component({
  selector: 'app-eg-header',
  standalone: true,
  templateUrl: './eg-header.component.html',
  styleUrls: ['./eg-header.component.scss'],
  imports: [MatBadgeModule, MatButtonModule, CommonModule, NotificationsComponent]
})
export class EgHeaderComponent {
  userEmail: string | null = '';
  imageUrl: string = '';
  givenName: string | null = null;
  familyName: string | null = null;
  private givenNameSubscription: Subscription | null = null;
  private familyNameSubscription: Subscription | null = null;
  isEligibilityAutoDetermined: boolean = false;
  isNotificationPanelVisible = false;
  noOfNotifications: number | string = 0;
  isRedNotificationDotVisible: boolean = false;
  isBellIconVisible: boolean = true;
  notificationAriaLabel = 'Notifications';
  isDropdownOpen = false;
  isNavbarOpen = false;
  logoFallback = false;
  readonly eligibilityBasePath = '/eligibility';
  activeTab: string = 'applications';

  brandingAgencyId: number | null = null;
  brandingAgencyName: string | null = null;
  brandingPrimaryLogo: string | null = null;
  brandingFavicon: string | null = null;
  brandingBrandLogo: string | null = null;
  brandingPrimaryColor: string | null = null;
  brandingSecondaryColor: string | null = null;

  get isRiderRoute(): boolean {
    return this.router.url.startsWith('/rider') || /^\/[^/]+\/rider/.test(this.router.url);
  }

  constructor(
    private readonly router: Router,
    private readonly userService: UserService,
    private readonly sharedCaseManagementService: SharedCaseManagementService,
    private readonly firebaseInitService: FirebaseInitService,
    private readonly ttauth: TTAuthCookieService,
    private readonly portalService: PortalService,
    private readonly elementRef: ElementRef,
    private readonly translationService: TranslationService
  ) {}

  translate(key: string): string {
    return this.translationService.translate(key);
  }

  ngOnInit(): void {
    this.userEmail = localStorage.getItem('userEmail');
    this.userService.isEligibilityAutoDetermined$.subscribe((isAutoDetermined) => {
      this.isEligibilityAutoDetermined = isAutoDetermined;
    });
    this.givenNameSubscription = this.userService.getGivenName().subscribe((givenName) => {
      if (givenName) {
        this.givenName = givenName;
      } else {
        this.givenName = sessionStorage.getItem('givenName');
      }
    });

    this.familyNameSubscription = this.userService.getFamilyName().subscribe((familyName) => {
      if (familyName) {
        this.familyName = familyName;
      } else {
        this.familyName = sessionStorage.getItem('familyName');
      }
    });

    this.brandingPrimaryLogo  = sessionStorage.getItem('brandingPrimaryLogo') || null;
    this.brandingBrandLogo    = sessionStorage.getItem('brandingBrandLogo') || null;
    this.brandingPrimaryColor = sessionStorage.getItem('brandingPrimaryColor') || null;

    this.sharedCaseManagementService.isBellIconVisible$.subscribe((isVisible: boolean) => {
      this.isBellIconVisible = isVisible;
    });

    this.sharedCaseManagementService.isRedNotificationDotVisible$.subscribe((isVisible: any) => {
      localStorage.setItem('red_notification_dot', String(isVisible));
      this.isRedNotificationDotVisible = isVisible;
      this.notificationAriaLabel = isVisible
        ? 'Notifications (new notification)'
        : 'Notifications';
    });
  }

  ngOnDestroy(): void {
    if (this.givenNameSubscription) {
      this.givenNameSubscription.unsubscribe();
    }
    if (this.familyNameSubscription) {
      this.familyNameSubscription.unsubscribe();
    }
  }

  setActiveTab(tab: string): void {
    this.activeTab = tab;
    this.isDropdownOpen = false;
    if (tab === 'applications') {
      const agencySlug = sessionStorage.getItem('agencySlug');
      const path = agencySlug ? `/${agencySlug}/rider/dashboard` : '/rider/dashboard';
      this.router.navigate([path]);
    }
  }

  navigateToDashboard(): void {
    this.isDropdownOpen = false;
    if (!this.isRiderRoute) return;
    const agencySlug = sessionStorage.getItem('agencySlug');
    const path = agencySlug ? `/${agencySlug}/rider/dashboard` : '/rider/dashboard';
    this.router.navigate([path]);
  }

  navigateToProfile(): void {
    this.isDropdownOpen = false;
    const agencySlug = sessionStorage.getItem('agencySlug');
    const path = agencySlug ? `/${agencySlug}/rider/profile` : '/rider/profile';
    this.router.navigate([path]);
  }

  navigateToSettings(): void {
    this.isDropdownOpen = false;
    const agencySlug = sessionStorage.getItem('agencySlug');
    const path = agencySlug ? `/${agencySlug}/rider/settings` : '/rider/settings';
    this.router.navigate([path]);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.isDropdownOpen && !this.elementRef.nativeElement.contains(event.target)) {
      this.isDropdownOpen = false;
    }
  }

  toggleDropdown() {
    this.isDropdownOpen = !this.isDropdownOpen;
  }

  toggleNavbar() {
    this.isNavbarOpen = !this.isNavbarOpen;
    this.isDropdownOpen = false;
  }

  // Unified logout handler – delegates to logoutTTAuth() for rider routes
  handleLogout(): void {
    this.isDropdownOpen = false;
    if (this.isRiderRoute) {      
    this.portalService.logUserActivity('LOGOUT').subscribe({
      error: (err) => console.error('Failed to log LOGOUT activity:', err),
    });
      this.logoutTTAuth();
    } else {
      this.logout();
    }
  }

  logout(): void {
    const removeItemIfExists = (storage: Storage, key: string) => {
      if (storage.getItem(key)) {
        storage.removeItem(key);
      }
    };

    const sessionKeys = ['givenName', 'familyName', 'applicationToken', 'isAdmin', 'userId', 'agencyId', 'agency', 'isSuperAdmin','isEligibilityAutoDetermined','caseId','caseStatusId','caseAdminAllowed','fcmToken','userRole','email'];
    const localKeys = ['applicationId'];

    sessionKeys.forEach(key => removeItemIfExists(sessionStorage, key));
    localKeys.forEach(key => removeItemIfExists(localStorage, key));
    EgNavBarComponent.resetInitialized();
    this.firebaseInitService.deleteFCMToken().then((deleted) => {
      if (deleted) {
        console.log('FCM token deleted successfully');
      } else {
        console.log('No FCM token to delete or error occurred');
      }
    });
    this.router.navigate(['/']);
  }

  logoutTTAuth(): void {
    const agencySlug = sessionStorage.getItem('agencySlug');
    const returnPath = agencySlug ? `/${agencySlug}/rider/login` : '/rider/login';
    sessionStorage.clear();
    localStorage.removeItem('applicationId');
    localStorage.removeItem('red_notification_dot');
    this.ttauth.logout(window.location.origin + this.eligibilityBasePath + returnPath);
  }

  onImageError(event: Event) {
    const target = event.target as HTMLImageElement;
    target.src = './../../assets/images/user.png';
  }

  onLogoError(event: Event) {
    const target = event.target as HTMLImageElement;
    target.src = 'assets/images/e-logo.png';
    this.logoFallback = true;
  }

  openNotificationPanel() {
    this.isDropdownOpen = false;
    this.isNotificationPanelVisible = true;
    this.sharedCaseManagementService.changeNotification(true);
    this.sharedCaseManagementService.setRedNotificationDotVisibility(false);
    localStorage.setItem('red_notification_dot', 'false');
  }

  onDropdownKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      (event.target as HTMLElement).click();
    }
  }
}
